import { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, FileText, X, Send, CheckCircle2, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import type { CollectClient } from '../lib/api';
import { ApiError } from '../lib/api';
import { MAX_SOURCE_FILE_BYTES, buildBundleFromFiles, isLikelyText } from '../lib/bundleBuilder';
import { templateDescription, templateLabel, templateOptions, templateTag } from '../lib/templates';
import { attachmentStatus, prefillFromExport, type HandoffState } from '../lib/runeHandoff';
import { HandoffNotice } from './HandoffNotice';
import { capsuleStorage } from '../lib/capsuleStorage';
import { DragOverlay } from './DragOverlay';

type Phase = 'idle' | 'building' | 'sending' | 'success' | 'error';

interface SendResult {
  capsule_id: number;
  name: string;
  status: string;
  storage_backend: string;
  storage_ref: string;
}

const DEFAULT_WORKSPACE = '_web';

export function SendCapsule({
  client,
  onSent,
  initialFiles,
  onFilesConsumed,
  initialTemplate,
  handoff,
}: {
  client: CollectClient;
  onSent: (result: SendResult) => void;
  initialFiles?: File[];
  onFilesConsumed?: () => void;
  /** Rune template slug from /send?template=<slug> (or /login?…&template=<slug>) — preselected. */
  initialTemplate?: string;
  /**
   * Set on /send?…&handoff=1: what the rune that opened this page sent (lib/runeHandoff.ts).
   * null while waiting. A received export prefills the form once; the visitor still presses Send.
   */
  handoff?: HandoffState | null;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [workspace, setWorkspace] = useState<string>(DEFAULT_WORKSPACE);
  const [purpose, setPurpose] = useState<string>('');
  const [template, setTemplate] = useState<string>(initialTemplate || '');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendResult | null>(null);
  const [dragHover, setDragHover] = useState(false);
  const [stats, setStats] = useState<{ raw: number; gzipped: number } | null>(null);

  // Whole-page drag-drop too
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes('Files')) setDragHover(true);
    };
    const onDragLeave = (e: DragEvent) => {
      // Leaves only count when leaving the window, not child elements
      if ((e.target as Element).nodeName === 'HTML') setDragHover(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragHover(false);
      const dropped = Array.from(e.dataTransfer?.files || []);
      if (dropped.length > 0) addFiles(dropped);
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  const addFiles = useCallback((newFiles: File[]) => {
    setError(null);
    const filtered = newFiles.filter((f) => {
      if (!isLikelyText(f)) {
        setError(`Skipped ${f.name} — not a text/markdown file. PDFs / DOCX need the CLI for conversion.`);
        return false;
      }
      if (f.size > MAX_SOURCE_FILE_BYTES) {
        setError(`Skipped ${f.name} — larger than 5 MB.`);
        return false;
      }
      return true;
    });
    setFiles((prev) => {
      // Dedupe by name+size
      const map = new Map(prev.map((f) => [`${f.name}::${f.size}`, f]));
      for (const f of filtered) map.set(`${f.name}::${f.size}`, f);
      return Array.from(map.values());
    });
  }, []);

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Consume initialFiles when set (e.g. from native Tauri drag-drop). Routes
  // through the same addFiles filter as browser drops so 5 MB cap + isLikelyText
  // + dedupe apply consistently.
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      addFiles(initialFiles);
      onFilesConsumed?.();
    }
  }, [initialFiles, addFiles, onFilesConsumed]);

  // Rune handoff: template = the rune, purpose = its title, the export as a JSON file. Once per
  // export, and only for the rune of this page's link (the receiver checked that too).
  const prefilled = useRef<unknown>(null);
  // The export's file as handed to the form: the notice says whether it is really in the list.
  const [handoffFile, setHandoffFile] = useState<{ name: string; size: number } | null>(null);
  useEffect(() => {
    if (!handoff || handoff.status !== 'received' || prefilled.current === handoff.payload) return;
    if (initialTemplate && handoff.payload.rune !== initialTemplate) return;
    prefilled.current = handoff.payload;
    const pre = prefillFromExport(handoff.payload);
    const file = new File([pre.file.text], pre.file.name, { type: pre.file.type });
    setTemplate(pre.template);
    setPurpose((p) => p || pre.purpose);
    setHandoffFile({ name: file.name, size: file.size });
    addFiles([file]);
  }, [handoff, initialTemplate, addFiles]);
  const handoffAttachment = handoffFile ? attachmentStatus(handoffFile, files) : undefined;

  const reset = () => {
    setFiles([]);
    setPurpose('');
    setPhase('idle');
    setError(null);
    setResult(null);
    setStats(null);
  };

  const send = async () => {
    if (files.length === 0) {
      setError('Add at least one file.');
      return;
    }
    if (!workspace.trim()) {
      setError('Workspace name is required.');
      return;
    }

    setError(null);
    setResult(null);

    try {
      setPhase('building');
      const built = await buildBundleFromFiles(workspace.trim(), files, { purpose: purpose.trim() || undefined });
      setStats({ raw: built.rawSize, gzipped: built.gzippedSize });

      setPhase('sending');
      const r = (await client.sendCapsule({
        workspaceId: workspace.trim(),
        manifest: template ? { ...built.manifest, template } : built.manifest,
        payloadB64: built.payloadB64,
        contentHash: built.contentHash,
        contentSize: built.contentSize,
        sourceType: 'collect-web-upload',
        tags: `collect,workspace:${workspace.trim()},web-upload,${files.length}-source${files.length === 1 ? '' : 's'}`
          + (template ? `,${templateTag(template)}` : ''),
      })) as SendResult;
      setResult(r);
      setPhase('success');
      onSent(r);
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : (e.message || String(e));
      setError(msg);
      setPhase('error');
    }
  };

  return (
    <div className="max-w-3xl">
      {/* Hero */}
      <div className="mb-10">
        <h1 className="display text-5xl mb-3">Send a capsule</h1>
        <p className="text-base" style={{ color: 'rgba(240,253,244,0.6)' }}>
          Drop text or markdown files anywhere on this page. Each file becomes a source inside a capsule
          and lands in your HIVE workspace. Bundle + SHA-256 + gzip happen entirely in your browser.
        </p>
        <p className="text-sm mt-2" style={{ color: 'rgba(240,253,244,0.45)' }}>
          The capsule is compressed, not encrypted. ML-KEM-768 encryption is only in the CLI for now:{' '}
          <code className="font-mono" style={{ color: '#34D399' }}>collect.py send-to-hive &lt;workspace&gt; --encrypt</code>.
        </p>
      </div>

      {/* Rune handoff — what the rune that opened this page sent */}
      {handoff !== undefined && initialTemplate && (
        <HandoffNotice state={handoff} template={initialTemplate} signedIn attachment={handoffAttachment} />
      )}

      {/* Template (rune) — preselected from ?template=<slug> */}
      <div className="glass-panel rounded-xl p-4 mb-4">
        <label className="label mb-2 block" htmlFor="send-template">
          Template <span style={{ textTransform: 'none', letterSpacing: 0, color: 'rgba(240,253,244,0.3)' }}>(optional)</span>
        </label>
        <select
          id="send-template"
          className="field"
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          disabled={phase === 'building' || phase === 'sending'}
        >
          <option value="">None — plain capsule</option>
          {templateOptions(template || undefined).map((slug) => (
            <option key={slug} value={slug}>{templateLabel(slug)} rune</option>
          ))}
        </select>
        <p className="text-xs mt-2" style={{ color: 'rgba(240,253,244,0.4)' }}>
          {template
            ? <>{templateDescription(template) ? `${templateDescription(template)} ` : ''}Recorded on the capsule as tag <code className="font-mono" style={{ color: '#34D399' }}>{templateTag(template)}</code>.</>
            : 'Pick a HIVE rune this capsule belongs to, or leave it plain.'}
        </p>
      </div>

      {/* Workspace + purpose row */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="glass-panel rounded-xl p-4">
          <label className="label mb-2 block">Workspace</label>
          <input
            className="field"
            type="text"
            placeholder="_web"
            value={workspace}
            onChange={(e) => setWorkspace(e.target.value)}
            spellCheck={false}
          />
          <p className="text-xs mt-2" style={{ color: 'rgba(240,253,244,0.4)' }}>
            Where the capsule lands. Use <code className="font-mono" style={{ color: '#34D399' }}>_web</code> for
            quick captures or pick an existing one (e.g. <code className="font-mono" style={{ color: '#34D399' }}>my-first-wiki</code>).
          </p>
        </div>
        <div className="glass-panel rounded-xl p-4">
          <label className="label mb-2 block">Purpose <span className="optional" style={{ textTransform: 'none', letterSpacing: 0, color: 'rgba(240,253,244,0.3)' }}>(optional)</span></label>
          <input
            className="field"
            type="text"
            placeholder="What is this collection of files about?"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
          />
          <p className="text-xs mt-2" style={{ color: 'rgba(240,253,244,0.4)' }}>
            Stored as <code className="font-mono" style={{ color: '#34D399' }}>purpose</code> on the bundle —
            helps future LLM passes stay in scope.
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <DropZone hover={dragHover} hasFiles={files.length > 0} onFiles={addFiles} />

      {/* File list */}
      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="space-y-2 mb-4 mt-4"
          >
            {files.map((f, i) => (
              <div key={`${f.name}::${f.size}`} className="glass-panel rounded-xl p-3 flex items-center gap-3">
                <FileText size={16} style={{ color: '#34D399' }} className="flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{f.name}</div>
                  <div className="text-xs" style={{ color: 'rgba(240,253,244,0.4)' }}>
                    {formatBytes(f.size)} · {f.type || 'text/plain'}
                  </div>
                </div>
                <button
                  onClick={() => removeFile(i)}
                  className="opacity-50 hover:opacity-100 transition-opacity p-1"
                  title="Remove"
                  disabled={phase === 'building' || phase === 'sending'}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status */}
      <AnimatePresence>
        {phase === 'success' && result && (
          <SuccessCard result={result} stats={stats} onReset={reset} />
        )}
        {phase === 'error' && error && <ErrorCard message={error} onDismiss={() => setError(null)} />}
        {error && phase === 'idle' && <ErrorCard message={error} onDismiss={() => setError(null)} />}
      </AnimatePresence>

      {/* Actions */}
      {phase !== 'success' && (
        <div className="flex items-center gap-3 mt-4">
          <button
            className="btn btn-primary"
            onClick={send}
            disabled={files.length === 0 || phase === 'building' || phase === 'sending'}
          >
            {phase === 'building' && <Loader2 size={14} className="animate-spin" />}
            {phase === 'sending' && <Loader2 size={14} className="animate-spin" />}
            {phase === 'idle' && <Send size={14} />}
            {phase === 'error' && <Send size={14} />}
            {phase === 'building' && 'Building bundle…'}
            {phase === 'sending' && 'Sending to HIVE…'}
            {(phase === 'idle' || phase === 'error') && `Send ${files.length || ''} file${files.length === 1 ? '' : 's'} to HIVE`}
          </button>
          {files.length > 0 && phase === 'idle' && (
            <button className="btn btn-ghost" onClick={reset}>
              Clear
            </button>
          )}
          <div className="flex-1" />
          {stats && phase === 'sending' && (
            <span className="label">
              {formatBytes(stats.raw)} → {formatBytes(stats.gzipped)} ({((stats.gzipped / stats.raw) * 100).toFixed(1)}%)
            </span>
          )}
        </div>
      )}

      {/* Drag-overlay (whole page) — shared component, also used by App.tsx for Tauri drag-drop */}
      <DragOverlay show={dragHover} label="Drop to send" workspace={workspace} />
    </div>
  );
}

function DropZone({ hover, hasFiles, onFiles }: { hover: boolean; hasFiles: boolean; onFiles: (f: File[]) => void }) {
  const [localHover, setLocalHover] = useState(false);
  return (
    <label
      className="glass-panel rounded-2xl p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-all"
      style={{
        borderStyle: 'dashed',
        borderColor: localHover || hover ? '#10B981' : 'rgba(16,185,129,0.3)',
        borderWidth: 2,
        background: localHover || hover ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.02)',
        minHeight: hasFiles ? 140 : 220,
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setLocalHover(true);
      }}
      onDragLeave={() => setLocalHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setLocalHover(false);
        const f = Array.from(e.dataTransfer.files);
        if (f.length > 0) onFiles(f);
      }}
    >
      <input
        type="file"
        multiple
        accept=".md,.markdown,.txt,.rst,.org,.log,.csv,.json,.yaml,.yml,.html,.htm,.xml,.tsv,.rtf,text/*,application/json,application/xml"
        className="hidden"
        onChange={(e) => {
          const f = Array.from(e.target.files || []);
          if (f.length > 0) onFiles(f);
          e.target.value = ''; // allow re-selecting same files
        }}
      />
      <Upload size={hasFiles ? 24 : 36} style={{ color: localHover || hover ? '#10B981' : 'rgba(16,185,129,0.5)', marginBottom: 12 }} className={localHover || hover ? 'heartbeat' : ''} />
      <div className="display text-2xl mb-1" style={{ color: '#F0FDF4' }}>
        {hasFiles ? 'Add more files' : 'Drop files here'}
      </div>
      <div className="text-sm" style={{ color: 'rgba(240,253,244,0.5)' }}>
        text · markdown · csv · json · yaml · html — up to 5 MB each
      </div>
      <div className="text-xs mt-2" style={{ color: 'rgba(240,253,244,0.35)' }}>
        or click to browse
      </div>
    </label>
  );
}

function SuccessCard({ result, stats, onReset }: { result: SendResult; stats: { raw: number; gzipped: number } | null; onReset: () => void }) {
  const imperioUrl = `https://qodoonect-production.up.railway.app/odoo/action-vat_services.action_hive_data_capsule/${result.capsule_id}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-panel-strong rounded-2xl p-6 mt-6"
      style={{ borderColor: '#10B981' }}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center heartbeat flex-none" style={{ background: 'linear-gradient(135deg, #10B981, #064E3B)' }}>
          <CheckCircle2 size={26} style={{ color: '#F0FDF4' }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="display text-2xl mb-1">Sent to HIVE</h3>
          <p className="text-sm mb-4" style={{ color: 'rgba(240,253,244,0.6)' }}>
            <span className="font-mono" style={{ color: '#34D399' }}>{result.name}</span> ·
            capsule #{result.capsule_id} ·
            {' '}<span className="label" style={{ color: '#34D399' }}>compressed, not encrypted</span> ·
            {' '}<span className="label" style={{ color: '#34D399' }}>{capsuleStorage(result.storage_backend).kind}</span>
          </p>
          {stats && (
            <p className="text-xs font-mono mb-4" style={{ color: 'rgba(240,253,244,0.4)' }}>
              raw: {formatBytes(stats.raw)} → gzipped: {formatBytes(stats.gzipped)} ({((stats.gzipped / stats.raw) * 100).toFixed(1)}%)
            </p>
          )}
          <div className="flex items-center gap-3">
            <a href={imperioUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
              <ExternalLink size={14} />
              Open in Imperio
            </a>
            <button className="btn btn-primary" onClick={onReset}>
              <Send size={14} />
              Send another
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ErrorCard({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="glass-panel rounded-xl p-4 mt-4 flex items-start gap-3"
      style={{ borderColor: 'rgba(239,68,68,0.4)' }}
    >
      <AlertCircle size={18} style={{ color: '#F87171' }} className="flex-none mt-0.5" />
      <div className="flex-1 text-sm" style={{ color: '#FCA5A5' }}>{message}</div>
      <button onClick={onDismiss} className="opacity-60 hover:opacity-100 flex-none mt-0.5">
        <X size={14} />
      </button>
    </motion.div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
